import React, { useState, useEffect } from 'react';
import { BookOpen, GraduationCap, Users, Target, Award, Brain, Play, ChevronDown, ChevronRight } from 'lucide-react';
import englishDataService from '../services/englishDataService';
import aiDrillService from '../services/aiDrillService';
import AiDrillProgressModal from './AiDrillProgressModal';
import DrillQuestions from './DrillQuestions';
import LoadingSpinner from './LoadingSpinner';

const PrimaryEnglish = () => {
  const [activeGrade, setActiveGrade] = useState('p1');
  const [drillState, setDrillState] = useState({
    isGenerating: false,
    drillQuestions: null,
    currentQuestion: null,
    currentTopic: null,
    currentQuestionIndex: null,
    currentTopicIndex: null,
    progress: {
      percentage: 0,
      currentStep: 0,
      elapsedTime: 0
    },
    metadata: null
  });

  // Supabase data states
  const [primaryEnglishData, setPrimaryEnglishData] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Collapse/Expand states
  const [expandedTopics, setExpandedTopics] = useState({}); // {topicIndex: true/false}
  const [expandedPurpose, setExpandedPurpose] = useState({}); // {topicIndex: true/false}
  const [expandedQuestions, setExpandedQuestions] = useState({}); // {topicIndex-qIndex: true/false}

  const grades = [
    { id: 'p1', label: '小學一年級', icon: BookOpen },
    { id: 'p2', label: '小學二年級', icon: BookOpen },
    { id: 'p3', label: '小學三年級', icon: BookOpen },
    { id: 'p4', label: '小學四年級', icon: BookOpen },
    { id: 'p5', label: '小學五年級', icon: BookOpen },
    { id: 'p6', label: '小學六年級', icon: BookOpen }
  ];

  // Load data from A2A/Supabase on component mount
  useEffect(() => {
    const loadPrimaryEnglishData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        
        // Use englishDataService which handles A2A with Supabase fallback
        const result = await englishDataService.getPrimaryGrammarData();
        const data = result?.data || result;
        
        // Ensure we have data structure with all grades
        if (!data || Object.keys(data).length === 0) {
          console.log('No data returned, falling back to mock data');
          const { primaryEnglishData: mockData } = await import('../data/primaryEnglishData');
          setPrimaryEnglishData(mockData);
        } else {
          // Verify all grades are present, fill missing ones with mock data
          const { primaryEnglishData: mockData } = await import('../data/primaryEnglishData');
          const grades = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
          const completeData = { ...data };
          
          grades.forEach(grade => {
            if (!completeData[grade] || Object.keys(completeData[grade]).length === 0) {
              console.log(`No data for grade ${grade}, using mock data`);
              completeData[grade] = mockData[grade] || {};
            }
          });
          
          setPrimaryEnglishData(completeData);
        }
      } catch (error) {
        console.error('Error loading primary English data:', error);
        setDataError(error.message);
        // Fallback to mock data on error
        const { primaryEnglishData: mockData } = await import('../data/primaryEnglishData');
        setPrimaryEnglishData(mockData);
      } finally {
        setDataLoading(false);
      }
    };

    loadPrimaryEnglishData();
  }, []);

  const currentGradeData = primaryEnglishData[activeGrade];

  // Initialize expanded state when grade changes
  useEffect(() => {
    if (currentGradeData && currentGradeData.topics) {
      // Expand all topics by default
      const topicsState = {};
      const purposeState = {};
      currentGradeData.topics.forEach((_, index) => {
        topicsState[index] = true;
        purposeState[index] = true;
      });
      setExpandedTopics(topicsState);
      setExpandedPurpose(purposeState);
    }
  }, [activeGrade, currentGradeData]);

  // Collapse/Expand functions
  const toggleTopic = (topicIndex) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicIndex]: !prev[topicIndex]
    }));
  };

  const togglePurpose = (topicIndex) => {
    setExpandedPurpose(prev => ({
      ...prev,
      [topicIndex]: !prev[topicIndex]
    }));
  };

  const toggleQuestion = (topicIndex, qIndex) => {
    const key = `${topicIndex}-${qIndex}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandAllTopics = () => {
    if (!currentGradeData || !currentGradeData.topics) return;
    const topicsState = {};
    const purposeState = {};
    const questionsState = {};
    currentGradeData.topics.forEach((topic, topicIndex) => {
      topicsState[topicIndex] = true;
      purposeState[topicIndex] = true;
      topic.questions?.forEach((_, qIndex) => {
        questionsState[`${topicIndex}-${qIndex}`] = true;
      });
    });
    setExpandedTopics(topicsState);
    setExpandedPurpose(purposeState);
    setExpandedQuestions(questionsState);
  };

  const collapseAllTopics = () => {
    if (!currentGradeData || !currentGradeData.topics) return;
    const topicsState = {};
    const purposeState = {};
    const questionsState = {};
    currentGradeData.topics.forEach((_, topicIndex) => {
      topicsState[topicIndex] = false;
      purposeState[topicIndex] = false;
    });
    setExpandedTopics(topicsState);
    setExpandedPurpose(purposeState);
    setExpandedQuestions(questionsState);
  };

  // Progress simulation for AI Drill
  useEffect(() => {
    let interval;
    if (drillState.isGenerating) {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(Math.floor(elapsed / 200), 100); // Simulate progress over 20 seconds
        const currentStep = Math.min(Math.floor(progress / 20), 4); // 5 steps total
        
        setDrillState(prev => ({
          ...prev,
          progress: {
            percentage: progress,
            currentStep: currentStep,
            elapsedTime: elapsed
          }
        }));
      }, 100);
    }
    return () => clearInterval(interval);
  }, [drillState.isGenerating]);

  const handleAiDrill = async (question, topic, questionIndex, topicIndex) => {
    setDrillState(prev => ({
      ...prev,
      isGenerating: true,
      currentQuestion: question,
      currentTopic: topic,
      currentQuestionIndex: questionIndex,
      currentTopicIndex: topicIndex,
      progress: {
        percentage: 0,
        currentStep: 0,
        elapsedTime: 0
      }
    }));

    try {
      const result = await aiDrillService.generateDrillQuestions(
        question,
        topic,
        currentGradeData.gradeName
      );

      setDrillState(prev => ({
        ...prev,
        isGenerating: false,
        drillQuestions: result.drillQuestions,
        metadata: result.metadata
      }));
    } catch (error) {
      console.error('AI Drill generation failed:', error);
      setDrillState(prev => ({
        ...prev,
        isGenerating: false
      }));
    }
  };

  const handleDrillComplete = () => {
    setDrillState(prev => ({
      ...prev,
      drillQuestions: null,
      currentQuestion: null,
      currentTopic: null,
      currentQuestionIndex: null,
      currentTopicIndex: null,
      metadata: null
    }));
  };

  const handleNewDrill = () => {
    if (drillState.currentQuestion && drillState.currentTopic) {
      handleAiDrill(
        drillState.currentQuestion, 
        drillState.currentTopic, 
        drillState.currentQuestionIndex, 
        drillState.currentTopicIndex
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Drill Progress Modal */}
      <AiDrillProgressModal
        isOpen={drillState.isGenerating}
        progress={drillState.progress}
        metadata={drillState.metadata}
      />

      {/* Loading State */}
      {dataLoading && (
        <div className="text-center py-12">
          <LoadingSpinner isLoading={true} />
          <div className="text-lg text-gray-600 mt-4">Loading primary English data...</div>
        </div>
      )}

      {/* Error State */}
      {dataError && !dataLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-600 mb-2">Error loading data: {dataError}</div>
          <div className="text-sm text-gray-600">Using fallback mock data</div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg">
        <div className="flex items-center space-x-3 mb-4">
          <GraduationCap className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Primary English Learning</h1>
        </div>
        <p className="text-blue-100 text-lg">
          Comprehensive English learning materials for Primary 1-6 students
        </p>
      </div>

      {/* Grade Navigation */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Grade Level</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {grades.map(grade => {
              const Icon = grade.icon;
              return (
                <button
                  key={grade.id}
                  onClick={() => setActiveGrade(grade.id)}
                  className={`flex flex-col items-center p-4 rounded-lg transition-all duration-200 ${
                    activeGrade === grade.id
                      ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-2 border-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium text-center">{grade.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grade Content */}
      {!dataLoading && currentGradeData && (
        <div className="space-y-6">

          {/* Grade Overview */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">{currentGradeData.gradeName}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-green-600" />
                  Learning Objectives
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {currentGradeData.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      {objective}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-purple-600" />
                  Key Skills
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {currentGradeData.keySkills.map((skill, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Topics Header with Collapse/Expand Controls */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">Topics</h2>
              <div className="flex gap-2">
                <button
                  onClick={expandAllTopics}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <ChevronDown className="w-4 h-4" />
                  Expand ALL
                </button>
                <button
                  onClick={collapseAllTopics}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  <ChevronRight className="w-4 h-4" />
                  Collapse ALL
                </button>
              </div>
            </div>
          </div>

          {/* Topics */}
          <div className="space-y-4">
            {currentGradeData.topics.map((topic, topicIndex) => {
              const isTopicExpanded = expandedTopics[topicIndex] !== false; // Default to true
              const isPurposeExpanded = expandedPurpose[topicIndex] !== false; // Default to true
              
              return (
              <div key={topicIndex} className="bg-white rounded-lg shadow-sm border">
                {/* Topic Header - Clickable to collapse/expand topic */}
                <button
                  onClick={() => toggleTopic(topicIndex)}
                  className="w-full p-6 border-b bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Topic {topicIndex + 1}: {topic.title}
                      </h3>
                      <p className="text-gray-600">{topic.description}</p>
                    </div>
                    <div className="ml-4">
                      {isTopicExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Topic Content - Collapsible */}
                {isTopicExpanded && (
                <div className="p-6">
                  {/* Purpose - Collapsible */}
                  <div className="mb-6">
                    <button
                      onClick={() => togglePurpose(topicIndex)}
                      className="w-full flex items-center justify-between mb-3 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                    >
                      <h4 className="text-lg font-semibold text-gray-700 flex items-center">
                        <Target className="w-5 h-5 mr-2 text-blue-600" />
                        目的 (Purpose)
                      </h4>
                      {isPurposeExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {isPurposeExpanded && (
                      <p className="text-gray-600 bg-blue-50 p-4 rounded-lg">
                        {topic.purpose}
                      </p>
                    )}
                  </div>

                  {/* Sample Questions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-green-600" />
                      Sample Questions & Answers
                    </h4>
                    <div className="space-y-4">
                      {topic.questions.map((question, qIndex) => {
                        const questionKey = `${topicIndex}-${qIndex}`;
                        const isQuestionExpanded = expandedQuestions[questionKey] !== false; // Default to true
                        
                        return (
                          <div key={qIndex} className="border rounded-lg bg-gray-50">
                            {/* Question Header - Clickable to collapse/expand */}
                            <button
                              onClick={() => toggleQuestion(topicIndex, qIndex)}
                              className="w-full p-4 hover:bg-gray-100 transition-colors text-left flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
                                  Q{qIndex + 1}
                                </span>
                                <span className="text-gray-700 font-medium">{question.question}</span>
                              </div>
                              {isQuestionExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                            
                            {/* Question Details - Collapsible */}
                            {isQuestionExpanded && (
                              <div className="p-4 pt-0 space-y-3">
                                <div>
                                  <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-2 py-1 rounded mr-2">
                                    Answer
                                  </span>
                                  <span className="text-gray-700">{question.answer}</span>
                                </div>
                                
                                <div>
                                  <span className="inline-block bg-purple-100 text-purple-800 text-sm font-medium px-2 py-1 rounded mr-2">
                                    Explanation
                                  </span>
                                  <p className="text-gray-600 text-sm mt-1">{question.explanation}</p>
                                </div>
                                
                                {/* AI Drill Button */}
                                <div className="mt-4 pt-3 border-t border-gray-200">
                                  <button
                                    onClick={() => handleAiDrill(question, topic, qIndex, topicIndex)}
                                    disabled={drillState.isGenerating}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                      drillState.isGenerating
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-purple-600 text-white hover:bg-purple-700'
                                    }`}
                                  >
                                    <Brain className="w-4 h-4" />
                                    <span>AI Drill</span>
                                    {drillState.isGenerating && drillState.currentQuestion === question && (
                                      <Play className="w-4 h-4 animate-pulse" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* AI Drill Questions - Show only for this specific question */}
                            {drillState.drillQuestions && 
                             drillState.currentQuestionIndex === qIndex && 
                             drillState.currentTopicIndex === topicIndex && (
                               <div className="bg-white rounded-lg border p-4 shadow-sm mt-4">
                                 <DrillQuestions
                                   drillQuestions={drillState.drillQuestions}
                                   onComplete={handleDrillComplete}
                                   onNewDrill={handleNewDrill}
                                 />
                               </div>
                             )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Data State */}
      {!dataLoading && !currentGradeData && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center text-gray-600">
            <p className="text-lg mb-2">No data available for {grades.find(g => g.id === activeGrade)?.label || activeGrade}</p>
            <p className="text-sm">Please try selecting a different grade level.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrimaryEnglish; 